const CASE_NAME_PATTERN = /\b([A-Z][A-Za-z0-9&.'/-]*(?:\s+[A-Z][A-Za-z0-9&.'/-]*){0,8}\s+v(?:s)?\.?\s+[A-Z][A-Za-z0-9&.'/-]*(?:\s+[A-Z][A-Za-z0-9&.'/-]*){0,8})\b/;

function slugify(value, fallback = 'general') {
  const slug = (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

function cleanDraftTitle(value) {
  return (value || '').replace(/^\[Draft\]\s*/, '').trim();
}

function detectCaseName(...texts) {
  for (const text of texts) {
    if (!text) continue;
    const match = text.match(CASE_NAME_PATTERN);
    if (match) return match[1].replace(/\s+/g, ' ').trim();
  }
  return '';
}

function inferWorkspaceType(user, itemType, tags) {
  const role = (user?.role || '').toUpperCase();
  const normalizedTags = new Set((tags || []).map(tag => String(tag).toUpperCase()));
  if (normalizedTags.has('PERSONAL') || normalizedTags.has('PRIVATE')) return 'PERSONAL';
  if (role === 'STUDENT') return 'CLASS';
  if (itemType === 'JUDGMENT') return 'PERSONAL';
  if (['ADVOCATE', 'CORPORATE_COUNSEL', 'PARALEGAL', 'JUDGE'].includes(role) && user?.institution) return 'FIRM';
  return 'PERSONAL';
}

function inferActivityType(user, itemType, agentType, researchData) {
  const researchType = (researchData?.type || '').toUpperCase();
  const role = (user?.role || '').toUpperCase();
  if (agentType === 'RESEARCH_AGENT') return 'DRAFTING';
  if (itemType === 'RESEARCH') {
    if (researchType === 'CASE') return 'CASE_ANALYSIS';
    if (researchType === 'STATUTE') return 'STATUTORY_RESEARCH';
    if (researchType === 'PERSON') return 'PROFILE_RESEARCH';
    if (role === 'STUDENT') return 'STUDY';
    return 'RESEARCH';
  }
  if (itemType === 'DOCUMENT') return 'DRAFTING';
  if (itemType === 'CASE_LAW') return 'CASE_RESEARCH';
  if (itemType === 'JUDGMENT') return 'MONITORING';
  return 'REFERENCE';
}

export function buildSavedItemWorkspace({
  user,
  itemType,
  title,
  content = '',
  tags = [],
  researchData = {},
  agentType = null,
  workspace = null,
}) {
  const baseTitle = cleanDraftTitle(title);
  const caseName = workspace?.case_name
    || researchData?.case_name
    || detectCaseName(baseTitle, researchData?.query, content, researchData?.full_report);
  const workspaceType = (workspace?.workspace_type || inferWorkspaceType(user, itemType, tags)).toUpperCase();
  const workspaceName = workspace?.workspace_name || (
    workspaceType === 'FIRM'
      ? (user?.institution || 'Firm Workspace')
      : workspaceType === 'CLASS'
        ? (user?.institution ? `${user.institution} Study Hub` : 'Class Workspace')
        : 'Personal Workspace'
  );
  const visibility = workspace?.visibility || (
    workspaceType === 'FIRM' && user?.institution ? 'SHARED_WORKSPACE' : 'PRIVATE'
  );
  const workspaceKey = workspace?.workspace_key || (
    visibility === 'SHARED_WORKSPACE'
      ? slugify(user?.institution)
      : `user-${slugify(user?.user_id, 'workspace')}`
  );
  let projectType = (workspace?.project_type || '').toUpperCase();
  if (!projectType) {
    if (caseName || (researchData?.type || '').toUpperCase() === 'CASE') projectType = 'CASE';
    else if (itemType === 'JUDGMENT') projectType = 'BRIEFING';
    else if (itemType === 'RESEARCH' || itemType === 'DOCUMENT') projectType = 'MATTER';
    else projectType = 'REFERENCE';
  }
  const projectName = workspace?.project_name
    || researchData?.project_name
    || researchData?.linked_doc_title
    || caseName
    || researchData?.query
    || baseTitle
    || 'Untitled Project';

  return {
    workspace_type: workspaceType,
    workspace_name: workspaceName,
    workspace_key: workspaceKey,
    visibility: visibility,
    project_type: projectType,
    project_name: projectName,
    project_key: workspace?.project_key || slugify(projectName),
    case_name: caseName || null,
    activity_type: workspace?.activity_type || inferActivityType(user, itemType, agentType, researchData),
    owner_name: workspace?.owner_name || user?.name || 'Unknown',
  };
}
